/**
 * Vista dedicada de todos los issues de Plane para un proyecto. La página
 * de detalle (/projects/:id) sólo muestra un preview de 12; aquí está la
 * lista completa con todos los filtros + paginación.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { PlaneRefreshHeader, type RefreshMeta } from "@/components/PlaneRefreshHeader";
import { IssuesList } from "@/features/projects/IssuesList";
import { api } from "@/lib/api";
import type { Project, PlaneIssueSummary } from "@axorial/shared";

interface ProjectMin {
  project: Project;
}

export const Route = createFileRoute("/projects/$projectId/issues")({
  component: ProjectIssuesPage,
});

function ProjectIssuesPage() {
  const { projectId } = Route.useParams();

  const projectQuery = useQuery<ProjectMin>({
    queryKey: ["project", projectId],
    queryFn: () => api.get(`/projects/${projectId}`),
  });

  const qc = useQueryClient();
  type IssuesResp = {
    issues: PlaneIssueSummary[];
    users: Record<string, { id: string; name: string; email: string }>;
    meta?: RefreshMeta;
    note?: string;
  };
  const issuesQuery = useQuery<IssuesResp>({
    queryKey: ["plane", "issues", projectId],
    queryFn: () => api.get(`/plane/projects/${projectId}/issues`),
    enabled: !!projectQuery.data?.project.planeProjectId,
  });
  const refresh = useMutation({
    mutationFn: () => api.get<IssuesResp>(`/plane/projects/${projectId}/issues?refresh=1`),
    onSuccess: (fresh) => qc.setQueryData(["plane", "issues", projectId], fresh),
  });

  if (!projectQuery.data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  const project = projectQuery.data.project;
  const total = issuesQuery.data?.issues.length ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={project.name}
        title="Todos los issues"
        subtitle={
          project.planeProjectId
            ? `${total} ${total === 1 ? "issue sincronizado" : "issues sincronizados"} desde Plane.`
            : "Este proyecto no está vinculado a Plane."
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to="/projects/$projectId" params={{ projectId }} className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Volver al proyecto
            </Link>
            {project.planeProjectId && (
              <a
                href={`https://tickets.axorial.mx/${project.planeWorkspaceSlug ?? "axorial"}/projects/${project.planeProjectId}/issues`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary btn-sm"
              >
                <ExternalLink className="size-3.5" /> Abrir en Plane
              </a>
            )}
          </div>
        }
      />

      {project.planeProjectId && (
        <PlaneRefreshHeader
          meta={issuesQuery.data?.meta ?? null}
          isPending={refresh.isPending}
          onRefresh={() => refresh.mutate()}
        />
      )}

      <div className="card-elevated">
        <div className="p-5 sm:p-6">
          {!project.planeProjectId ? (
            <p className="text-sm text-base-content/60">
              Vincula este proyecto con Plane desde la página de edición para ver sus issues.
            </p>
          ) : issuesQuery.isLoading ? (
            <div className="grid place-items-center py-12">
              <span className="loading loading-spinner text-primary" />
            </div>
          ) : (
            <IssuesList issues={issuesQuery.data?.issues ?? []} users={issuesQuery.data?.users} />
          )}
        </div>
      </div>
    </div>
  );
}
